import { useMemo } from 'react'
import { TechnicianAppointmentsItem } from '@myapp/contract'

export interface ResolvedBreak {
  startTimeIso: string
  overrideId: number | null
}

export function resolveBreakForDay(
  item: TechnicianAppointmentsItem,
  dayIso: string,
): ResolvedBreak {
  const dayDate = new Date(dayIso)

  // Check for override on this day
  const override = item.breakOverrides.find((entry) => {
    const entryDate = new Date(entry.startTime)
    return (
      entryDate.getFullYear() === dayDate.getFullYear() &&
      entryDate.getMonth() === dayDate.getMonth() &&
      entryDate.getDate() === dayDate.getDate()
    )
  })

  if (override) {
    return {
      startTimeIso: override.startTime,
      overrideId: override.id,
    }
  }

  // Project defaultBreakStart onto the current day.
  // Break times are stored as "local time labelled UTC" (e.g. 17:00 local → T17:00Z),
  // so we must read them with getUTC* and place into a local-time constructor
  // so that getLeftPercent (which uses getHours) shows the correct position.
  if (!item.defaultBreakStart) {
    return { startTimeIso: new Date(`${dayIso}T12:00:00`).toISOString(), overrideId: null }
  }
  const defaultBreak = new Date(item.defaultBreakStart)
  const projected = new Date(
    dayDate.getFullYear(),
    dayDate.getMonth(),
    dayDate.getDate(),
    defaultBreak.getUTCHours(),
    defaultBreak.getUTCMinutes(),
    0,
    0,
  )

  return {
    startTimeIso: projected.toISOString(),
    overrideId: null,
  }
}

export function useBreakBlocks(
  technicians: TechnicianAppointmentsItem[] | undefined,
  dayIso: string,
): Map<string, ResolvedBreak> {
  return useMemo(() => {
    const map = new Map<string, ResolvedBreak>()
    if (!technicians) return map

    for (const tech of technicians) {
      map.set(tech.technicianId, resolveBreakForDay(tech, dayIso))
    }
    return map
  }, [technicians, dayIso])
}
